import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useSelector, useDispatch } from 'react-redux';

// app: actions
import saveUserSettings from 'actions/user/saveUserSettings';

// app: constants
import {
  CULINARY_TOPIC_OPTIONS,
  CULINARY_REGION_OPTIONS,
} from 'constants/culinary-interest-options';

// assets
import CssUserSettings from '../UserSettings.scss';
import CssNutritionSettings from '../NutritionSettings/NutritionSettings.scss';

export function OptionMultiSelect({ name, options, selectedIds, onChange, disabled }) {
  useStyles(CssNutritionSettings);

  const toggle = id => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter(selectedId => selectedId !== id)
        : [...selectedIds, id],
    );
  };

  // An option flagged `newRow` starts a new row of buttons.
  const rows = options.reduce((acc, option) => {
    if (option.newRow || !acc.length) acc.push([]);
    acc[acc.length - 1].push(option);
    return acc;
  }, []);

  return (
    <div className="nutrition-settings__options" role="group" aria-label={name}>
      {rows.map(row => (
        <div className="nutrition-settings__row" key={row[0].id}>
          {row.map(option => {
            const isSelected = selectedIds.includes(option.id);
            return (
              <button
                type="button"
                key={option.id}
                className={classNames('nutrition-settings__button', { active: isSelected })}
                onClick={() => toggle(option.id)}
                aria-pressed={isSelected}
                disabled={disabled}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

OptionMultiSelect.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, label: PropTypes.string, newRow: PropTypes.bool }),
  ).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

OptionMultiSelect.defaultProps = {
  disabled: false,
};

function CulinarySettings() {
  useStyles(CssUserSettings, CssNutritionSettings);
  const dispatch = useDispatch();
  const settings = useSelector(state => state.user.settings);
  const settingsResolved = useSelector(state => state.user.settingsResolved);

  const topicIds = Array.isArray(settings?.culinary_topics) ? settings.culinary_topics : [];
  const regionIds = Array.isArray(settings?.culinary_regions) ? settings.culinary_regions : [];

  return (
    <div>
      <p>This helps us to give you the most relevant search results.</p>
      <div className="nutrition-settings__group">
        <div className="nutrition-settings__group-label">Categories</div>
        <OptionMultiSelect
          name="Categories"
          options={CULINARY_TOPIC_OPTIONS}
          selectedIds={topicIds}
          onChange={ids => dispatch(saveUserSettings({ culinary_topics: ids }))}
          disabled={!settingsResolved}
        />
      </div>
      <div className="nutrition-settings__group">
        <div className="nutrition-settings__group-label">Regions</div>
        <OptionMultiSelect
          name="Regions"
          options={CULINARY_REGION_OPTIONS}
          selectedIds={regionIds}
          onChange={ids => dispatch(saveUserSettings({ culinary_regions: ids }))}
          disabled={!settingsResolved}
        />
      </div>
    </div>
  );
}

export default CulinarySettings;
